import type { ExtractionCitation } from '@ctw/shared/api/ai/use-extraction-jobs';
import {
  getDocumentReferencesForBinaryIds,
  getProvenanceTargetIds,
  getSourceBinaryId,
} from '@ctw/shared/api/fhir/binaries';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { getResourceDate } from '@ctw/shared/api/fhir/resource-date';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import { searchProvenancesFQS } from '@ctw/shared/api/fqs/queries/provenances';
import { collectServiceResolvedCitationIds } from '@ctw/shared/content/fact-extraction/export-citation-resolution';
import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import { resourceDrawerActionForModel } from '@ctw/shared/context/_internal/resource/resource-drawer';
import {
  type FqsHotstorageRequestOptions,
  fetchResourceByTypeAndIds,
  useFqsHotstorageRequestOptions,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import {
  type UsePatientQueryFnContext,
  usePatientQuery,
} from '@ctw/shared/context/patient-provider';
import { type CustomQueryFnContext, DEFAULT_STALE_TIME } from '@ctw/shared/hooks/use-ctw-query';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { isFetchableResourceType } from '@ctw/shared/utils/fhir/resource-mappings';
import { FQS_HOTSTORAGE_REQUEST_OPTIONS } from '@ctw/shared/utils/hotstorage-header';
import { retryWithExponentialBackoff } from '@ctw/shared/utils/request';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import type { QueryClient } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';
import type { Provenance, Resource } from 'fhir/r4';
import { compact, groupBy, uniq } from 'lodash-es';
import { useMemo } from 'react';

type Context = UsePatientQueryFnContext<CustomQueryFnContext>;

/**
 * How a citation can be surfaced to the user:
 * - `document`: a source document, opens in the document viewer.
 * - `drawer`: the cited resource itself, opens in the resource drawer.
 * Citations that resolve to neither are omitted (their document icon is greyed out).
 */
export type ResolvedCitation =
  | { kind: 'document'; binaryId: string; label: string; date?: string }
  | { kind: 'drawer'; model: FHIRModel<Resource>; label: string; date?: string };

/**
 * What a citation should resolve to:
 * - `resource`: the cited resource where the app can render it (the drawer),
 *   with a document only for a DocumentReference citation. No provenance
 *   lookups. This is what the in-app citation icon opens.
 * - `document`: the source document behind the citation, following Lens
 *   `aggregatedFrom` and Provenance, with the drawer as a last resort. This is
 *   what the export bundle needs.
 */
export type ClientResolutionTarget = 'resource' | 'document';

export interface ClientCitationResolution {
  sources: Map<number, ResolvedCitation>;
  // Citations whose resource-type group still failed after retries. Their
  // sources are unknown, not absent.
  failedCitationIds: Set<number>;
}

// Stable identities for the no-citations case so consumers' memos don't
// rebuild every render while the query is disabled or loading.
const EMPTY_SOURCE_MAP: ReadonlyMap<number, ResolvedCitation> = new Map();
const EMPTY_CITATION_IDS: ReadonlySet<number> = new Set();

// Resolution fans out into several FQS calls at once and is prone to transient
// 429s and dropped connections. Each group retries with backoff (1s, then 2s)
// before its failure is allowed to surface.
const RESOLVE_RETRIES = 2;
const RESOLVE_RETRY_BASE_DELAY_MS = 500;

// Citation resource types don't share a typed date getter (some models expose
// `dateCreated`, others `recordedDate`, others a `dateDisplay` that can be a
// range), so read the resource's primary date generically and format it.
function citationResourceDate(model: FHIRModel<Resource>): string | undefined {
  const iso = getResourceDate(model.resource);
  return iso ? formatFHIRDate(iso) : undefined;
}

function toDocumentSource(
  model: FHIRModel<Resource>,
  binaryId: string | undefined,
): ResolvedCitation | undefined {
  if (binaryId) {
    return { kind: 'document', binaryId, label: model.title, date: citationResourceDate(model) };
  }
  return toDrawerSource(model);
}

function toDrawerSource(model: FHIRModel<Resource>): ResolvedCitation | undefined {
  if (resourceDrawerActionForModel(model as never)) {
    return { kind: 'drawer', model, label: model.title, date: citationResourceDate(model) };
  }
  return undefined;
}

// The cited resource itself. A DocumentReference citation is its document, so
// it opens in the viewer; everything else opens in the resource drawer.
function toResourceSource(model: FHIRModel<Resource>): ResolvedCitation | undefined {
  if (model instanceof DocumentModel) {
    return toDocumentSource(model, model.binaryId);
  }
  return toDrawerSource(model);
}

// Resolves a citation to its own resource's surface, or nothing when the
// resource has none (its icon greys out).
function toResourceCitation(
  citation: ExtractionCitation,
  modelById: Map<string, FHIRModel<Resource>>,
): Array<readonly [number, ResolvedCitation]> {
  const model = modelById.get(citation.resourceId);
  const source = model ? toResourceSource(model) : undefined;
  return source ? [[citation.id, source] as const] : [];
}

// FQS answers `Type(id:)` for a target it can't retrieve with a "resource not
// found" error, and when every target in a batch is missing the whole request
// fails with no data. A missing target has no provenance to offer, so an
// all-not-found response is an empty result, not a lookup that MUST be retried.
function isAllResourceNotFound(error: unknown): boolean {
  return (
    error instanceof AggregateError &&
    error.errors.length > 0 &&
    error.errors.every((cause) => {
      const message = (cause as { message?: unknown } | undefined)?.message;
      return typeof message === 'string' && message.includes('resource not found');
    })
  );
}

async function searchProvenancesForTargets(
  context: Context,
  resourceType: ResourceTypeString,
  targetIds: Array<string>,
): Promise<Array<Provenance>> {
  try {
    return await searchProvenancesFQS(
      context.telemetry,
      context.graphqlClient,
      resourceType,
      targetIds,
    );
  } catch (error) {
    if (isAllResourceNotFound(error)) {
      return [];
    }
    throw error;
  }
}

// A DocumentReference citation opens the document viewer, which titles itself
// from the DocumentReference it reverse-resolves off the Binary — for a CDA,
// that's usually the full-document DR, not the cited section DR. Resolve the
// label from that same reverse lookup so the hover/inline name matches the
// opened document's title (one batched provenance + DR fetch for all the
// binaries). Falls back to the cited resource's own title if the lookup can't
// resolve, so this never greys a citation that would otherwise open.
async function resolveDocumentCitations(
  citations: Array<ExtractionCitation>,
  modelById: Map<string, FHIRModel<Resource>>,
  context: Context,
): Promise<Array<readonly [number, ResolvedCitation]>> {
  const citedSource = (citation: ExtractionCitation) => toResourceCitation(citation, modelById);

  const binaryIds = uniq(
    compact(
      citations.map((citation) => {
        const model = modelById.get(citation.resourceId);
        return model instanceof DocumentModel ? model.binaryId : undefined;
      }),
    ),
  );
  if (binaryIds.length === 0) {
    return citations.flatMap(citedSource);
  }

  let documentByBinaryId: Map<string, DocumentModel>;
  try {
    const documents = await getDocumentReferencesForBinaryIds({
      binaryIds,
      graphqlClient: context.graphqlClient,
      telemetry: context.telemetry,
      options: FQS_HOTSTORAGE_REQUEST_OPTIONS,
    });
    documentByBinaryId = new Map(documents.map((doc) => [doc.binaryId ?? '', doc]));
  } catch {
    // Reverse resolution failed — keep the cited resource's own title rather
    // than failing the whole group.
    return citations.flatMap(citedSource);
  }

  return citations.flatMap((citation) => {
    const model = modelById.get(citation.resourceId);
    if (!(model instanceof DocumentModel && model.binaryId)) {
      return citedSource(citation);
    }
    const document = documentByBinaryId.get(model.binaryId);
    const source: ResolvedCitation = {
      kind: 'document',
      binaryId: model.binaryId,
      label: document?.displayTitle ?? model.title,
      date: citationResourceDate(document ?? model),
    };
    return [[citation.id, source] as const];
  });
}

// Resolves all citations of one resource type using a single batched resource
// fetch and, for the `document` target on non-document types, a single
// provenance search. Citations whose resource type isn't fetchable resolve to
// nothing and grey out.
async function resolveCitationGroup(
  resourceType: string,
  citations: Array<ExtractionCitation>,
  target: ClientResolutionTarget,
  context: Context,
  fqsHotstorageRequestOptions: FqsHotstorageRequestOptions,
  queryClient: QueryClient,
): Promise<Array<readonly [number, ResolvedCitation]>> {
  if (!isFetchableResourceType(resourceType)) {
    return [];
  }
  const resourceIds = citations.map((citation) => citation.resourceId) as NonEmptyArray<string>;
  const models = (await fetchResourceByTypeAndIds({
    resourceType,
    resourceIds,
    telemetry: context.telemetry,
    ctwFetch: context.ctwFetch,
    graphqlClient: context.graphqlClient,
    queryClient,
    requestContext: context.requestContext,
    patient: context.patient,
    fqsHotstorageRequestOptions,
  })) as Array<FHIRModel<Resource>>;
  if (models.length === 0) {
    return [];
  }

  const modelById = new Map(models.map((model) => [model.id, model]));
  if (target === 'resource') {
    if (resourceType === 'DocumentReference') {
      return resolveDocumentCitations(citations, modelById, context);
    }
    return citations.flatMap((citation) => toResourceCitation(citation, modelById));
  }

  const binaryByModelId = new Map<string, string>();

  if (resourceType === 'DocumentReference') {
    // DocumentReference models carry their Binary id directly — no provenance lookup needed.
    for (const model of models) {
      if (model instanceof DocumentModel && model.binaryId) {
        binaryByModelId.set(model.id, model.binaryId);
      }
    }
  } else {
    // Summary/lens resources have no provenance of their own; getProvenanceTargetIds
    // follows them to the underlying source resources. Search provenance for the
    // union of targets in one call, then resolve each model's source Binary.
    // Models that carry their source Binary directly (data-acquisition meta
    // extension) are excluded from the provenance search since getSourceBinaryId
    // short-circuits on them — saves the call when every model has the extension.
    const modelsNeedingProvenance = models.filter((model) => !model.sourceBinaryId);
    const targetIds = uniq(modelsNeedingProvenance.flatMap(getProvenanceTargetIds));
    const provenances =
      targetIds.length > 0
        ? await searchProvenancesForTargets(context, resourceType as ResourceTypeString, targetIds)
        : [];
    for (const model of models) {
      const binaryId = getSourceBinaryId(provenances, model);
      if (binaryId) {
        binaryByModelId.set(model.id, binaryId);
      }
    }
  }

  return citations.flatMap((citation) => {
    const model = modelById.get(citation.resourceId);
    if (!model) {
      return [];
    }
    const source = toDocumentSource(model, binaryByModelId.get(model.id));
    return source ? [[citation.id, source] as const] : [];
  });
}

/**
 * Eagerly resolves each citation to how it can be surfaced (document viewer,
 * resource drawer, or neither), per `target`. Citations are batched by
 * resource type so the number of network calls scales with the number of
 * distinct resource types, not the number of citations. A group that still
 * fails after retries does not take the others down: its citations are
 * reported in `failedCitationIds` and the result is not kept fresh, so the
 * next mount retries them.
 */
export function useClientResolvedCitations(
  citations: Array<ExtractionCitation>,
  target: ClientResolutionTarget,
) {
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();
  const queryClient = useQueryClient();

  const query = usePatientQuery<ClientCitationResolution>({
    queryId: 'FactExtractionCitationSources',
    queryKey: [target, citations.map((c) => `${c.id}:${c.resourceType}:${c.resourceId}`).join(',')],
    queryFn: async (context) => {
      const citationsByType = Object.entries(
        groupBy(citations, (citation) => citation.resourceType),
      );
      const outcomes = await Promise.allSettled(
        citationsByType.map(([resourceType, group]) =>
          retryWithExponentialBackoff(
            () =>
              resolveCitationGroup(
                resourceType,
                group,
                target,
                context,
                fqsHotstorageRequestOptions,
                queryClient,
              ),
            RESOLVE_RETRIES,
            RESOLVE_RETRY_BASE_DELAY_MS,
          ),
        ),
      );
      const sources = new Map<number, ResolvedCitation>();
      const failedCitationIds = new Set<number>();
      outcomes.forEach((outcome, index) => {
        const [resourceType, group] = citationsByType[index];
        if (outcome.status === 'fulfilled') {
          for (const [citationId, source] of outcome.value) {
            sources.set(citationId, source);
          }
          return;
        }
        context.telemetry.trackError({
          message: 'Failed to resolve fact extraction citation sources',
          error: outcome.reason as Error,
          context: { resourceType, citationCount: group.length },
        });
        for (const citation of group) {
          failedCitationIds.add(citation.id);
        }
      });
      return { sources, failedCitationIds };
    },
    // A result with failures MUST NOT be served from cache as if it were
    // complete; the next mount re-resolves.
    staleTime: (cached) =>
      (cached.state.data?.failedCitationIds.size ?? 0) > 0 ? 0 : DEFAULT_STALE_TIME,
    enabled: citations.length > 0,
  });

  // A whole-query failure has no per-citation detail, so every citation counts
  // as failed and consumers need only the one set.
  const failedCitationIds = useMemo<ReadonlySet<number>>(() => {
    if (query.isError) {
      return new Set(citations.map((citation) => citation.id));
    }
    return query.data?.failedCitationIds ?? EMPTY_CITATION_IDS;
  }, [query.isError, query.data, citations]);
  return {
    clientResolvedByCitationId: query.data?.sources ?? EMPTY_SOURCE_MAP,
    isLoading: citations.length > 0 && query.isLoading,
    isError: failedCitationIds.size > 0,
    failedCitationIds,
  };
}

/**
 * Source-document resolution for an export. The service already supplied a
 * document for most citations during the job; only the citations it left open
 * on some value are resolved client-side.
 */
export function useClientResolvedCitationsForExport(
  groups: Array<MappedFactGroup>,
  citations: Array<ExtractionCitation>,
) {
  const openCitations = useMemo(() => {
    const settledCitationIds = collectServiceResolvedCitationIds(groups);
    return citations.filter((citation) => !settledCitationIds.has(citation.id));
  }, [groups, citations]);
  return useClientResolvedCitations(openCitations, 'document');
}
